import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  ViewContainerRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { take } from 'rxjs';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { UPLOAD_PROGRESS_SOURCE } from '../domain/entities/upload-progress.port';
import {
  creepToward,
  formatUploadSize,
  initialUploadProgress,
  nextUploadProgress,
} from '../domain/entities/upload-progress.math';
import { FailedUploadsDialogComponent, type FailedUploadsDialogContext } from './failed-uploads-dialog.component';

const TICK_MS = 16;

// Whose failures have already been announced. The lock hangs on the SOURCE, not on the bar: a host
// can keep several bars mounted over one upload coordinator (the onboarding wizard holds a step per
// photo group, all of them alive so their previews survive a tab switch), and a per-instance lock
// let one failure open one window per mounted bar — three identical dialogs and three retryAll on a
// single failed file. Keyed by identity and weak, so a source that goes away takes its entry with it.
const failuresAnnouncedFor = new WeakSet<object>();
// The failure toast hangs on the source for the same reason: one toast per upload, however many bars.
const failureToastFor = new WeakMap<object, number>();
// The toast stands above the dialog overlay, so its «Подробнее» stays clickable over an open window:
// one window per source, or a second «Повторить всё» re-sends the same files twice.
const failureWindowOpenFor = new WeakSet<object>();
// Names sent again by «Повторить загрузку»: one of them failing again means a retry does not help, so the
// next window offers support. Closing the window without a retry, or a run that ends clean, forgets them.
const retriedNamesFor = new WeakMap<object, ReadonlySet<string>>();

/**
 * The platform's one upload bar: track, counter, phase word, inline cancel and the failure toast.
 * Products feed it through `UPLOAD_PROGRESS_SOURCE` and mount it once — the shell variant is the
 * window-bottom bar, the inline variant sits inside a host that reserves its own room.
 */
@Component({
  selector: 'upload-progress-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, HlmTooltip],
  // Folded away it is still in the DOM for the 600ms transform, so `inert` takes it out of the
  // a11y tree and the tab order — otherwise every cabinet screen carries an idle «0 из 0 МБ»
  // progressbar that a screen reader announces.
  host: { '[class]': 'hostClass()', '[attr.inert]': 'shown() ? null : ""' },
  template: `
    <div
      class="mx-auto flex w-full max-w-250 items-center gap-3"
      [class.px-4]="variant() === 'shell'"
      [class.py-3]="variant() === 'shell'"
    >
      <div
        class="h-0.75 min-w-0 flex-1 overflow-hidden rounded-full bg-line2"
        role="progressbar"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-valuenow]="Math.round(painted())"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class="h-full rounded-full bg-acc" [style.width.%]="painted()"></div>
      </div>

      @if (asking()) {
        <span class="flex flex-none items-center gap-2 text-xs">
          <span class="text-c3">Отменить?</span>
          <button type="button" class="text-acc focus-visible:underline" (click)="confirmCancel()">Да</button>
          <button type="button" class="text-c3 focus-visible:underline" (click)="asking.set(false)">Нет</button>
        </span>
      } @else {
        <!-- Auto width: the track runs up to the status text. Tabular digits hold the width while the digit count
             stays; the track moves only when a number gains a digit or the status word changes. -->
        <span class="flex flex-none items-center gap-2 text-xs tabular-nums">
          @if (stalled()) {
            <span class="text-c3">Нет связи</span>
          } @else if (finished()) {
            <app-icon name="check" [size]="14" class="text-ok" />
            <span class="text-ok" role="status">Готово</span>
          } @else if (label()) {
            <span class="text-c2">{{ label() }}</span>
          } @else {
            <span class="text-c2">{{ counter() }}</span>
            @if (showPhaseWord()) {
              <span class="text-c3">{{ phaseWord() }}</span>
            }
          }
          @if (canCancel()) {
            <button
              type="button"
              class="grid size-3.5 flex-none place-items-center rounded-full text-c3 transition-colors
                duration-[var(--t-fast)] hover:text-ink"
              aria-label="Отменить загрузку"
              hlmTooltip="Отменить загрузку"
              (click)="asking.set(true)"
            >
              <app-icon name="close" [size]="14" />
            </button>
          }
        </span>
      }
    </div>
  `,
})
export class UploadProgressBarComponent {
  private readonly source = inject(UPLOAD_PROGRESS_SOURCE);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(BrnDialogService);
  private readonly vcr = inject(ViewContainerRef);
  private readonly zone = inject(NgZone);
  private readonly toast = inject(ToastService);

  /** `shell` = the window-bottom bar mounted once per cabinet; `inline` = inside a host's own strip. */
  readonly variant = input<'shell' | 'inline'>('inline');
  /** Off = the counter alone, flush against the cancel cross. */
  readonly showPhaseWord = input(true);
  /**
   * Off = the bar says nothing about failures: no toast, no window — the host names the failed file
   * on its own tile and retries it there.
   */
  readonly showFailures = input(true);
  /** Replaces the counter and the phase word; «Нет связи» and «Готово» still win. */
  readonly label = input<string | null>(null);
  /** Off = no cancel cross: the host's run cannot be stopped from the bar. */
  readonly cancellable = input(true);
  readonly ariaLabel = input('Загрузка фото');

  protected readonly Math = Math;
  private readonly state = signal(initialUploadProgress());
  protected readonly painted = signal(0);
  protected readonly asking = signal(false);
  private mounted = false;
  private reduced: MediaQueryList | undefined;
  private timer: ReturnType<typeof setInterval> | null = null;
  private published = -1;

  protected readonly stalled = this.source.stalled;
  protected readonly failureCount = computed(() => (this.showFailures() ? this.source.failures().length : 0));
  protected readonly finished = computed(() => this.state().phase === 'done');
  /**
   * Public: an inline host that reserves room for the bar opens and closes with this. The failed run is
   * terminal and the failures live on the toast — the bar would otherwise park forever on a half-filled
   * track, since `visible` stays true for a failed phase.
   */
  readonly shown = computed(() => this.state().visible && this.state().phase !== 'failed');

  protected readonly hostClass = computed(() => {
    const shell =
      'fixed inset-x-0 bottom-0 z-30 border-t border-line2 bg-panel ' +
      'transition-[var(--t-prop-motion)] duration-[var(--t-fold)] ease-[var(--ease-smooth)] motion-reduce:transition-none';
    const base = this.variant() === 'shell' ? shell : 'block';
    // Collapse is the same movement as the growth, downward — never a display flip.
    const folded = this.variant() === 'shell' ? 'translate-y-full opacity-0' : 'hidden';
    return this.shown() ? base : `${base} ${folded}`;
  });

  /** Byte phase reads «155 из 220 МБ»; processing reads «12 из 37». */
  protected readonly counter = computed(() =>
    this.state().phase === 'processing'
      ? `${Math.min(this.source.previewsReady(), this.source.previewsTotal())} из ${this.source.previewsTotal()}`
      : formatUploadSize(this.source.bytesSent(), this.source.bytesTotal()),
  );

  protected readonly phaseWord = computed(() => (this.state().phase === 'processing' ? 'обработка' : 'загрузка'));

  /** Nothing to cancel while the connection is gone: the queue would clear with the server still ingesting. */
  protected readonly canCancel = computed(() => this.cancellable() && this.shown() && !this.finished() && !this.stalled());

  constructor() {
    afterNextRender(() => {
      this.reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      if (this.variant() === 'shell') window.addEventListener('resize', this.publishHeight);
      this.destroyRef.onDestroy(() => {
        this.stop();
        window.removeEventListener('resize', this.publishHeight);
        document.documentElement.style.removeProperty('--upload-bar-h');
      });
      this.mounted = true;
      this.start();
    });
    // New truth (numbers, failures) wakes the ticker; it parks itself once nothing animates (spec 10).
    effect(() => {
      this.source.snapshot();
      this.source.failures();
      untracked(() => this.start());
    });
    effect(() => {
      const count = this.failureCount();
      untracked(() => this.syncFailureToast(count));
    });
    // «Подробнее» opens the window through this bar, so the toast leaves with it.
    this.destroyRef.onDestroy(() => this.syncFailureToast(0));
  }

  /**
   * One sticky toast per source carries the count: a new count rewrites it, zero takes it down. Closed
   * by the cross, it comes back only when the count moves again — a new failure is new news.
   */
  private syncFailureToast(count: number): void {
    if (!this.showFailures()) return;
    const id = failureToastFor.get(this.source);
    const live = id !== undefined && this.toast.toasts().some(t => t.id === id);
    if (count === 0) {
      if (live) this.toast.dismiss(id);
      failureToastFor.delete(this.source);
      return;
    }

    const title = `Не загрузилось фотографий: ${count}`;
    if (live) this.toast.update(id, title);
    else
      failureToastFor.set(
        this.source,
        this.toast.action(title, 'Подробнее', () => this.openFailures(), 'error', 0, true),
      );
  }

  /** Outside the zone: 60 ticks a second must not run change detection, the signals it writes do. */
  private start(): void {
    if (!this.mounted || this.timer !== null) return;
    this.timer = this.zone.runOutsideAngular(() => setInterval(() => this.tick(), TICK_MS));
  }

  private stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private tick(): void {
    const prev = this.state();
    const next = nextUploadProgress(prev, this.source.snapshot(), Date.now());
    this.state.set(next);

    // A run that reopened (added files, a late failure) drops its cancel question with it.
    if (this.asking() && !this.cancellable()) this.asking.set(false);
    // Not on an emptied list: a retry empties it while the same files are back on the wire.
    if (next.phase === 'done' && this.failureCount() === 0) retriedNamesFor.delete(this.source);
    this.autoOpenFailures();

    // A fresh batch (after the latch or an emptied queue) starts at 0 — never a retreat from the old 100 (spec 9).
    if (next.phase === 'idle' || (prev.latched && !next.latched)) this.painted.set(0);
    const target = next.target;
    const current = this.painted();
    if (current !== target) {
      const crept = creepToward(current, target, { allowRetreat: target < current });
      this.painted.set(this.reduced?.matches ? target : crept);
    }

    // The fold is a transform, which no ResizeObserver reports — measuring on the same tick
    // moves the page's padding in step with the bar instead of snapping after it.
    const shell = this.variant() === 'shell';
    if (shell) this.publishHeight();
    // Parked when nothing animates: painted sits on its target, the bar is folded and (shell) has left the window.
    if (this.painted() === target && !this.shown() && (!shell || this.published === 0)) this.stop();
  }

  /**
   * The failure window surfaces itself once per run and once per SOURCE — at the FIRST terminal
   * failure, never on a redraw and never once per mounted bar; it re-arms when the failures are
   * gone (a new batch). The gate is the failing FILE, not
   * the whole batch: keying it on the run's terminal `failed` phase starved the window for as long
   * as any sibling photo was still on the wire — the readiness poll runs for minutes — while the
   * failure row had already replaced the track. Whether it opened at all came down to which file
   * happened to settle last.
   */
  private autoOpenFailures(): void {
    // A bar that shows no failures also holds no opinion on the source's lock.
    if (!this.showFailures()) return;
    if (this.failureCount() === 0) {
      failuresAnnouncedFor.delete(this.source);
      return;
    }

    if (failuresAnnouncedFor.has(this.source)) return;
    failuresAnnouncedFor.add(this.source);
    this.zone.run(() => this.openFailures());
  }

  protected openFailures(): void {
    if (failureWindowOpenFor.has(this.source)) return;
    failureWindowOpenFor.add(this.source);
    const source = this.source;
    const items = computed(() =>
      this.source.failures().map(entry => ({ name: entry.name, reason: entry.reason, retryable: entry.retryable })),
    );
    const retried = retriedNamesFor.get(source);
    const repeated = computed(() => !!retried && items().some(item => retried.has(item.name)));
    const ref = this.dialog.open<FailedUploadsDialogContext, boolean>(
      FailedUploadsDialogComponent,
      this.vcr,
      { items, repeated },
      { hasBackdrop: true },
    );
    ref.closed$.pipe(take(1)).subscribe(retry => {
      failureWindowOpenFor.delete(source);
      if (!retry) {
        retriedNamesFor.delete(source);
        return;
      }

      retriedNamesFor.set(
        source,
        new Set(
          items()
            .filter(item => item.retryable !== false)
            .map(item => item.name),
        ),
      );
      source.retryAll();
    });
  }

  protected confirmCancel(): void {
    this.asking.set(false);
    if (this.stalled()) return;
    this.source.cancel();
  }

  /**
   * The bar's height is variable, so whatever must sit above it reads ONE
   * published number. Published is the room taken from the bottom edge, so no consumer learns the
   * bar's geometry, only how much of the window it occupies. Folded away, the transform puts the box
   * below the edge and the number reads 0 on its own.
   */
  private readonly publishHeight = (): void => {
    const top = this.host.nativeElement.getBoundingClientRect().top;
    const occupied = Math.max(0, Math.min(window.innerHeight, Math.round(window.innerHeight - top)));
    if (occupied === this.published) return;

    this.published = occupied;
    document.documentElement.style.setProperty('--upload-bar-h', `${occupied}px`);
  };
}
